import * as React from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  TextField,
  Typography,
} from "@mui/material";
import { CheckCircle, Error, Warning } from "@mui/icons-material";
import PdfViewer from "./PdfViewer";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import FruitifyIcon from "../../../shared-components/FruitifyIcons";
import { useApiClient } from "../../../api";
import { RetryExtractionButton } from "./RetryExtractionButton";

export const EditExtractedTextDialog = ({
  open,
  setOpen,
  chosenAnswer,
  answer,
  setAnswer,
  onSave,
  saving = false,
  disableSave = false,
  onRetrySuccess,
  disableRetry = false,
  readOnly = false,
}) => {
  const [showImage, setShowImage] = useState(
    Boolean(chosenAnswer?.handwrittenAnswerFile?.preSignedUrl),
  );
  const [approving, setApproving] = useState(false);
  const [locallyApproved, setLocallyApproved] = useState(false);

  const qualityReview =
    chosenAnswer?.handwrittenAnswerFile?.extractionQualityReview;

  const isApproved =
    qualityReview?.qualityLevel === "approved" || locallyApproved;

  const ApiClient = useApiClient();

  const handleApprove = async () => {
    if (!chosenAnswer?.handwrittenAnswerFile?.id) return;

    setApproving(true);
    try {
      const result = await ApiClient.assignment.approveExtraction({
        handwrittenFile: chosenAnswer.handwrittenAnswerFile.id,
      });

      if (result.success) {
        setLocallyApproved(true);
        enqueueSnackbar("Extraction approved successfully", {
          variant: "success",
        });
      } else {
        enqueueSnackbar(result.message || "Failed to approve extraction", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar(`Error: ${error.message}`, { variant: "error" });
    } finally {
      setApproving(false);
    }
  };

  useEffect(() => {
    setShowImage(Boolean(chosenAnswer?.handwrittenAnswerFile?.preSignedUrl));
  }, [chosenAnswer?.handwrittenAnswerFile?.preSignedUrl]);

  const pdfUrl = chosenAnswer?.handwrittenAnswerFile?.preSignedUrl || null;

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      fullWidth
      maxWidth={"xl"}
      slotProps={{
        paper: {
          component: "form",
          sx: {
            backgroundImage: "none",
            height: { xs: "90vh", md: "90vh" },
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      <DialogTitle>
        {readOnly ? "Review extraction" : "Extracted Text"}
      </DialogTitle>
      <DialogContent
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <Grid
          container
          spacing={2}
          direction={"row"}
          alignItems={"stretch"}
          sx={{ flex: 1, minHeight: 0 }}
        >
          <Grid
            size={{
              xs: showImage && pdfUrl ? 6 : 12,
              md: showImage && pdfUrl ? 6 : 12,
            }}
          >
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
              sx={{ mt: 1 }}
            >
              {pdfUrl && (
                <Button
                  variant={showImage ? "contained" : "outlined"}
                  color="primary"
                  size="small"
                  onClick={() => setShowImage((v) => !v)}
                  startIcon={
                    <FruitifyIcon
                      name={showImage ? "VisibilityOff" : "Visibility"}
                    />
                  }
                >
                  {showImage ? "Hide Image" : "Show Image"}
                </Button>
              )}
            </Stack>

            <FormControl fullWidth>
              <TextField
                label={"Student answer"}
                multiline
                fullWidth
                minRows={12}
                maxRows={28}
                margin="normal"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                slotProps={{ input: { readOnly } }}
                sx={{ flexGrow: 1 }}
              />
            </FormControl>

            {qualityReview?.reviewSuggested && !isApproved && (
              <Alert
                severity={
                  qualityReview.qualityLevel === "low" ? "error" : "warning"
                }
                icon={
                  qualityReview.qualityLevel === "low" ? <Error /> : <Warning />
                }
                sx={{ mt: 2, alignItems: "center" }}
                action={
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleApprove}
                    disabled={approving}
                    startIcon={<CheckCircle />}
                  >
                    {approving ? "Approving..." : "Approve"}
                  </Button>
                }
              >
                <Typography variant="body2" fontWeight="medium">
                  {qualityReview.qualityReason}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  Review the extracted text and make any corrections needed.
                  Once satisfied, click "Approve" to confirm.
                </Typography>
              </Alert>
            )}

            {isApproved && (
              <Alert
                severity="success"
                icon={<CheckCircle />}
                sx={{ mt: 2, alignItems: "center" }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    disabled
                    startIcon={<CheckCircle />}
                  >
                    Approved
                  </Button>
                }
              >
                <Typography variant="body2" fontWeight="medium">
                  Extraction has been reviewed and approved
                </Typography>
              </Alert>
            )}
          </Grid>
          {showImage && pdfUrl && (
            <Grid size={{ xs: 6, md: 6 }}>
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                }}
              >
                <PdfViewer pdfUrl={pdfUrl} />
              </div>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        {!readOnly && chosenAnswer?.handwrittenAnswerFile && onRetrySuccess && (
          <RetryExtractionButton
            answer={chosenAnswer}
            onSuccess={onRetrySuccess}
            disabled={disableRetry}
          />
        )}
        <Button onClick={() => setOpen(false)} variant={"outlined"}>
          Cancel
        </Button>
        {onSave && !readOnly && (
          <Button
            onClick={onSave}
            disabled={saving || disableSave}
            variant={"contained"}
          >
            {saving ? "Saving..." : "Done"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
